import {
  createWebsiteBodySchema,
  discountCodeSchema,
  type BillingInterval,
  type CreateWebsiteBody,
  type PlanId,
} from '@groundly/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Minus, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { usePaidPlanCheck, usePlans } from '../../../api/billing.js';
import { useCreateWebsite } from '../../../api/websites.js';
import billingStyles from '../../../billing/billing.module.css';
import { CycleStartSummary } from '../../../billing/CycleStartSummary.js';
import { PaidPlanGate } from '../../../billing/PaidPlanGate.js';
import { PlanPicker } from '../../../billing/PlanPicker.js';
import { Button } from '../../../components/Button.js';
import { DangerAlert } from '../../../components/DangerAlert.js';
import { PageHeading } from '../../../components/PageHeading.js';
import { LoadFailedArea, LoadingArea } from '../../../components/PlaceholderPage.js';
import { TextField } from '../../../components/TextField.js';
import styles from '../../../components/page.module.css';
import layout from './new-website.module.css';

/**
 * Guided step 1: create a website with name, domains and plan (`flow.md`,
 * A2). The plan starts today and its first billing cycle is prorated to the
 * 4th (`product/business-rules.md`, Billing). Every new website needs the
 * account's unpaid invoices paid and a card on file first, whatever plan is
 * chosen, even Free (`PaidPlanGate`, `context="create"`; user, 2026-11-02);
 * adding the card goes through Paddle's checkout, which sends the payer back
 * here with `?card=added`, so the form is kept in session storage meanwhile.
 * On success, navigates to `/websites/$websiteId/setup?step=2`.
 */
const searchSchema = z.object({ card: z.enum(['added']).optional() });

export const Route = createFileRoute('/_app/websites/new')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: NewWebsitePage,
});

/** The form while the payer is away adding a card. */
const DRAFT_KEY = 'groundly:new-website-draft';

function readDraft(): FormValues | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw === null ? null : (JSON.parse(raw) as FormValues);
  } catch {
    return null;
  }
}

function writeDraft(values: FormValues | null): void {
  try {
    if (values === null) sessionStorage.removeItem(DRAFT_KEY);
    else sessionStorage.setItem(DRAFT_KEY, JSON.stringify(values));
  } catch {
    // Without storage the form simply starts empty after adding a card.
  }
}

/** The form uses an array of objects for `domains` so `useFieldArray` works. */
interface FormValues {
  name: string;
  domains: Array<{ value: string }>;
  planId: PlanId | null;
  billingInterval: BillingInterval;
  discountCode: string;
}

function NewWebsitePage() {
  const navigate = useNavigate();
  const { card } = Route.useSearch();
  const create = useCreateWebsite();
  const plans = usePlans();
  const paidPlanCheck = usePaidPlanCheck({ awaitingCard: card === 'added' });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: (card === 'added' ? readDraft() : null) ?? {
      name: '',
      domains: [{ value: '' }],
      planId: null,
      billingInterval: 'monthly',
      discountCode: '',
    },
  });
  useEffect(() => {
    const subscription = watch((values) => writeDraft(values as FormValues));
    return () => subscription.unsubscribe();
  }, [watch]);
  // A discount code replaces founding pricing, so the plans show their list prices.
  const withCode = watch('discountCode').trim() !== '';
  const chosenPlanId = watch('planId');
  const chosenPlan = plans.data?.items.find((p) => p.id === chosenPlanId) ?? null;
  // A new website always needs a card on file, even on Free: it waits for the
  // check and whatever it asks for. If the check can't load, the server
  // still refuses what it must.
  const blocked =
    chosenPlan !== null &&
    (paidPlanCheck.isPending || (paidPlanCheck.isSuccess && paidPlanCheck.data.blocker !== 'none'));
  // A draft saved before yearly billing existed has no interval: monthly.
  const interval = watch('billingInterval') ?? 'monthly';
  const setBillingInterval = (value: BillingInterval) => setValue('billingInterval', value);

  const { fields, append, remove } = useFieldArray({ control, name: 'domains' });

  function onSubmit(values: FormValues) {
    if (values.planId === null) return;
    const body: CreateWebsiteBody = {
      name: values.name.trim(),
      domains: values.domains.map((d) => d.value),
      planId: values.planId,
      // Free has nothing to pay, so it is always monthly.
      billingInterval: values.planId === 'free' ? 'monthly' : values.billingInterval,
      ...(values.discountCode.trim() ? { discountCode: values.discountCode.trim() } : {}),
    };

    // Validate with shared schema before sending
    const parsed = createWebsiteBodySchema.safeParse(body);
    if (!parsed.success) return;

    create.mutate(parsed.data, {
      onSuccess: (website) => {
        writeDraft(null);
        void navigate({
          to: '/websites/$websiteId/setup',
          params: { websiteId: website.id },
          search: { step: 2 },
        });
      },
    });
  }

  return (
    <>
      <PageHeading>Add website</PageHeading>

      <form
        className={layout.page}
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <p className={styles.text}>Step 1 of 4 &middot; Website name, domains and plan</p>

        <section
          className={`${billingStyles.card} ${layout.details}`}
          aria-labelledby="details-title"
        >
          <h2 id="details-title" className={billingStyles.cardTitle}>
            Website
          </h2>
          <TextField
            id="name"
            label="Website name"
            placeholder="My website"
            autoFocus
            {...register('name', { required: 'Enter a website name.' })}
            error={errors.name?.message}
          />

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Domains</legend>
            {fields.map((field, index) => (
              <div key={field.id} className={styles.domainRow}>
                <TextField
                  id={`domain-${index}`}
                  label={`Domain ${index + 1}`}
                  placeholder="example.com"
                  {...register(`domains.${index}.value` as const, {
                    required: 'Enter a domain.',
                  })}
                  error={errors.domains?.[index]?.value?.message}
                />
                {fields.length > 1 ? (
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Remove domain ${index + 1}`}
                    onClick={() => remove(index)}
                  >
                    <Minus size={16} />
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className={styles.addButton}
              onClick={() => append({ value: '' })}
            >
              <Plus size={16} /> Add domain
            </button>
          </fieldset>
        </section>

        <section className={`${billingStyles.card} ${layout.plan}`} aria-labelledby="plan-title">
          <div>
            <h2 id="plan-title" className={billingStyles.cardTitle}>
              Plan
            </h2>
            <p className={billingStyles.muted}>
              Each website has its own plan, sized by AI replies. Founding prices stay the same for
              life. You can change the plan later in the website&apos;s Settings.
            </p>
          </div>

          {plans.isError ? (
            <LoadFailedArea onRetry={() => void plans.refetch()} />
          ) : plans.isPending ? (
            <LoadingArea />
          ) : (
            <Controller
              control={control}
              name="planId"
              rules={{ required: 'Choose a plan.' }}
              render={({ field, fieldState }) => (
                <PlanPicker
                  plans={plans.data.items}
                  name="plan"
                  legend="Choose a plan"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  founding={!withCode}
                  interval={interval}
                  onIntervalChange={setBillingInterval}
                  notes={false}
                />
              )}
            />
          )}

          {plans.isSuccess ? (
            <CycleStartSummary
              plan={chosenPlan}
              founding={!withCode}
              withCode={withCode}
              interval={interval}
            />
          ) : null}

          {chosenPlan !== null && paidPlanCheck.isSuccess ? (
            <PaidPlanGate
              check={paidPlanCheck.data}
              returnedFromCard={card === 'added'}
              context="create"
            />
          ) : null}

          <div className={layout.narrow}>
            <TextField
              id="discount-code"
              label="Discount code (optional)"
              autoComplete="off"
              hint="With a code, this website pays the plan's list price less the code's discount, instead of the founding price."
              {...register('discountCode', {
                validate: (code) =>
                  code.trim() === '' ||
                  discountCodeSchema.safeParse(code).success ||
                  'A discount code is 3 to 32 letters, digits, - or _.',
              })}
              error={errors.discountCode?.message}
            />
          </div>
        </section>

        {create.isError ? (
          <DangerAlert title="Couldn't create the website">
            {(create.error as Error).message}
          </DangerAlert>
        ) : null}

        <div className={layout.actions}>
          <Button variant="primary" type="submit" disabled={create.isPending || blocked}>
            {create.isPending ? 'Creating...' : 'Continue'}
          </Button>
        </div>
      </form>
    </>
  );
}
